import React, { useState, useEffect } from "react";
import {
    DataGrid,
    GridColDef,
    GridRowClassNameParams,
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Select,
    MenuItem,
    SelectChangeEvent,
} from "@mui/material";
import {
    Wrapper,
    HeaderWrapper,
    Header,
    DataGridWrapper,
} from "./UserTeamsMapping.styles";
import {
    TeamsDetailRead,
    UserTeamMappingResponse,
} from "../../api/generated";
import {
    ArrowBackOutlined,
} from "@mui/icons-material";
// @ts-ignore
import EditIcon from "../../assets/Edit.svg?react"
// @ts-ignore
import SaveIcon from '../../assets/Check.svg?react';
// @ts-ignore
import CancelIcon from '../../assets/X.svg?react';
import { useUpdateUserTeamMapping } from "../../hooks/Teams";
import { useQueryClient } from "@tanstack/react-query";
import useShowToast from '../../hooks/useShowToast';
import useShowError from "../../hooks/useShowError";
import {
    useGetUserTeams,
} from "../../hooks/Teams"

export interface Iprops {
    formMode: string;
    setFormMode: React.Dispatch<React.SetStateAction<"display" | "create" | "edit" | "teamsMap" | "bulkupload">>;
    onCancel: () => void;
    DataForteamsDropDown: Array<TeamsDetailRead>;
}

export default function UserTeamMapping({
    formMode,
    setFormMode,
    onCancel,
    DataForteamsDropDown,
}: Iprops) {
    const {data, isLoading, isError, error } = useGetUserTeams();
    const updatedUserTeamMapdata = useUpdateUserTeamMapping().mutate;
    const [rows, setRows] = useState([]);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editedTeams, setEditedTeams] = useState<Record<number, number[]>>({});
    const queryClient = useQueryClient()
    const showSuccessToast = useShowToast()
     const showError = useShowError()

    useEffect(()=>{
        setRows(data);
    },[data])

    // Handle start editing
    const handleEditButton = (member: UserTeamMappingResponse) => {
        setEditingRowId(member.id);
        setEditedTeams((prev) => ({
            ...prev,
            [member.id]: member.teams.map((team) => team.id),
        }));
    };

    // Handle team selection change
    const onChangeMemberTeam = (
        member: UserTeamMappingResponse,
        value: number[]
    ) => {
        setEditedTeams((prev) => ({
            ...prev,
            [member.id]: value,
        }));
    };

    const handleSaveButton = (member: UserTeamMappingResponse) => {
        const teamIds = editedTeams[member.id] || [];
        const USER_TEAM_MAPPING = "userTeamMapping"
        updatedUserTeamMapdata(
            {
                id: member.id,
                payload: { team_ids: teamIds }, // assuming UserTeamMappingUpdate = { teams: number[] }
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [USER_TEAM_MAPPING] })
                    setEditingRowId(null);
                    showSuccessToast("Successfully added teams")
                },
                onError: () => {
                    showError('Failed to update user teams');
                },
            }
        );
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingRowId(null);
    };

    const handleCancelButton = () => {
        setFormMode("display");
    };

    const columns: Array<GridColDef> = [
        {
            field: "username",
            headerName: "UserName",
            flex: 1,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: "name",
            headerName: "First Name",
            flex: 0.7,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: "last_name",
            headerName: "Last Name",
            flex: 0.7,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: "teams",
            headerName: "Teams",
            width: 160,
            disableColumnMenu: true,
            sortable: false,
            renderCell: ({ row: member }) => {
                const isEditing = editingRowId === member.id;
                if (isEditing) {
                    return (
                        <Select
                            sx={{ width: 150 }}
                            multiple
                            value={editedTeams[member.id] || []}
                            onChange={(e: SelectChangeEvent<number[]>) => {
                                const value = typeof e.target.value === "string"
                                    ? e.target.value.split(",").map(Number)
                                    : (e.target.value as number[]);
                                onChangeMemberTeam(member, value);
                            }}
                            size="small"
                            renderValue={(selected) =>
                                DataForteamsDropDown
                                    .filter((team) => selected.includes(team.id))
                                    .map((team) => team.name)
                                    .join(", ")
                            }
                        >
                            {DataForteamsDropDown.map((team) => (
                                <MenuItem key={team.id} value={team.id}>
                                    {team.name}
                                </MenuItem>
                            ))}
                        </Select>
                    );
                }
                return Array.isArray(member.teams)
                    ? member.teams.map((team: TeamsDetailRead) => team.name).join(", ")
                    : "";
            },
        },
        {
            field: "action",
            headerName: "Action",
            align: "center",
            headerAlign: "left",
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row: member }) => {
                const isEditing = editingRowId === member.id;
                if (isEditing) {
                    return (
                        <>
                            <SaveIcon
                                style={{ cursor: "pointer", marginRight: '16px' }}
                                onClick={() => handleSaveButton(member)}
                            />
                            <CancelIcon
                                style={{ cursor: "pointer", }}
                                onClick={handleCancelEdit}
                            />
                        </>
                    );
                }
                return (
                    <EditIcon
                        style={{ cursor: "pointer", }}
                        onClick={() => handleEditButton(member)}
                    />
                );
            },
        },
    ];

    const noDataStyles = {
        height: data.length < 1 ? 600 : "",
        width: data.length < 1 ? "100%" : "",
    };

    if (isLoading) {
        return <p>Loading opco's...</p>;
    }

    if (isError) {
        return <p>Error loading opco's: {error?.message}</p>;
    }

    return (
        <Wrapper>
            <HeaderWrapper>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "row",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Header sx={{ display: "inline" }}>
                            Team Mapping
                        </Header>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Button
                            onClick={handleCancelButton}
                            variant="outlined"
                            startIcon={<ArrowBackOutlined />}
                        >
                            Existing users
                        </Button>
                    </Box>
                </Box>
            </HeaderWrapper>
            <DataGridWrapper>
                <div style={noDataStyles}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        hideFooterPagination={true}
                        getRowClassName={(
                            e: GridRowClassNameParams<any>
                        ) =>
                            e.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
                        }
                        sx={{
                            border: "none",
                            "& .MuiDataGrid-cell, & .MuiDataGrid-root, & .MuiDataGrid-columnHeaders":
                            {
                                borderBottom: "none",
                            },
                            "& .MuiDataGrid-cell": {
                                padding: "0",
                            },
                            "& .MuiDataGrid-cell:first-of-type": {
                                padding: "0 0 0 50px",
                            },
                            "& .MuiDataGrid-cell:last-child": {
                                padding: "0 50px 0 0",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                padding: "0",
                            },
                            "& .MuiDataGrid-columnHeader:first-of-type": {
                                padding: "0 0 0 50px",
                            },
                            "& .MuiDataGrid-row.even-row": {
                                backgroundColor: 'var(--background-off-white)',
                            },
                            "& .MuiDataGrid-footerContainer": {
                                display: "none",
                            },
                            "& .MuiDataGrid-cellContent": {
                                fontSize: "13px",
                            },
                        }}
                    />
                </div>
            </DataGridWrapper>
        </Wrapper>
    );
}
